import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import type {
  TypeDate,
  TypeTimeOfDay,
  TypeDateTime,
  TypeTimeZone,
} from '@/generated/hooks/bishop/models';
import i18n from '@/shared/i18n';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isoWeek);

// ============================================================================
// Conventions and Default Behavior
// ============================================================================
/**
 * Default Value Conventions:
 *
 * - 0 vs undefined:
 *   - 0 is treated as a valid value and will be formatted as '00' or preserved as 0
 *   - undefined means "not provided" and will use sensible defaults
 *
 * - Default values for undefined:
 *   - Year: '0000' (for ISO formatting)
 *   - Month/Day: '01' (for ISO formatting)
 *   - Hours/Minutes: '00' (for ISO formatting)
 *
 * - ISO Formatting:
 *   - Preserves 0 values as '00' (e.g., month: 0 → '00')
 *   - undefined becomes defaults (e.g., month: undefined → '01')
 *
 * - Display Formatting:
 *   - 0 values are treated as "not provided" and may be omitted from display
 *   - Functions like formatDateDisplay check for > 0 before displaying components
 */

// ============================================================================
// Constants
// ============================================================================

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// ============================================================================
// Date Format Tokens (for use with formatDate and dayjs)
// ============================================================================
/**
 * Date format tokens for use with `formatDate()` function.
 * Based on dayjs format tokens: https://day.js.org/docs/en/display/format
 *
 * Token Reference:
 * - YYYY: 4-digit year (2025)
 * - YY: 2-digit year (25)
 * - MMMM: Full month name (December)
 * - MMM: Abbreviated month name (Dec)
 * - MM: 2-digit month (01-12)
 * - M: Month number (1-12)
 * - DD: 2-digit day (01-31)
 * - D: Day number (1-31)
 * - dddd: Full weekday name (Monday)
 * - ddd: Abbreviated weekday name (Mon)
 * - HH: 24-hour format (00-23)
 * - hh: 12-hour format (01-12)
 * - h: 12-hour format without padding (1-12)
 * - mm: Minutes (00-59)
 * - ss: Seconds (00-59)
 * - A: AM/PM
 * - a: am/pm
 */
export const DATE_FORMAT_TOKENS = {
  /** Short date: "Dec 9, 2025" */
  SHORT_DATE: 'MMM D, YYYY',
  /** Short date with time: "Dec 9, 3:30 PM" */
  SHORT_DATE_TIME: 'MMM D, h:mm A',
  /** Compact date with time: "8/29/25 4:23 PM" - for tight spaces */
  COMPACT_DATE_TIME: 'M/D/YY h:mm A',
} as const;

/** Type for date format token keys */
export type TDateFormatToken = keyof typeof DATE_FORMAT_TOKENS;

/** Type for date format token values */
export type TDateFormatString = (typeof DATE_FORMAT_TOKENS)[TDateFormatToken];

/**
 * Comprehensive mapping of IANA timezone IDs to their abbreviations
 * Used for displaying human-readable timezone names
 */
const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  // UTC
  UTC: 'UTC',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'GMT',

  // Americas - North America
  'America/New_York': 'EST/EDT',
  'America/Chicago': 'CST/CDT',
  'America/Denver': 'MST/MDT',
  'America/Los_Angeles': 'PST/PDT',
  'America/Phoenix': 'MST',
  'America/Anchorage': 'AKST/AKDT',
  'America/Honolulu': 'HST',
  'America/Toronto': 'EST/EDT',
  'America/Vancouver': 'PST/PDT',
  'America/Edmonton': 'MST/MDT',
  'America/Winnipeg': 'CST/CDT',
  'America/Halifax': 'AST/ADT',
  'America/St_Johns': 'NST/NDT',
  'America/Mexico_City': 'CST/CDT',
  'America/Cancun': 'EST',
  'America/Monterrey': 'CST/CDT',
  'America/Tijuana': 'PST/PDT',

  // Americas - Central America
  'America/Guatemala': 'CST',
  'America/Belize': 'CST',
  'America/San_Jose': 'CST',
  'America/Managua': 'CST',
  'America/Tegucigalpa': 'CST',
  'America/Panama': 'EST',

  // Americas - Caribbean
  'America/Havana': 'CST/CDT',
  'America/Jamaica': 'EST',
  'America/Port-au-Prince': 'EST/EDT',
  'America/Santo_Domingo': 'AST',
  'America/Puerto_Rico': 'AST',
  'America/Barbados': 'AST',

  // Americas - South America
  'America/Sao_Paulo': 'BRT/BRST',
  'America/Buenos_Aires': 'ART',
  'America/Santiago': 'CLT/CLST',
  'America/Lima': 'PET',
  'America/Bogota': 'COT',
  'America/Caracas': 'VET',
  'America/Montevideo': 'UYT',
  'America/Asuncion': 'PYT/PYST',
  'America/La_Paz': 'BOT',
  'America/Guayaquil': 'ECT',

  // Europe - Western
  'Europe/London': 'GMT/BST',
  'Europe/Dublin': 'GMT/IST',
  'Europe/Lisbon': 'WET/WEST',
  'Atlantic/Reykjavik': 'GMT',
  'Atlantic/Azores': 'AZOT/AZOST',

  // Europe - Central
  'Europe/Paris': 'CET/CEST',
  'Europe/Berlin': 'CET/CEST',
  'Europe/Rome': 'CET/CEST',
  'Europe/Madrid': 'CET/CEST',
  'Europe/Amsterdam': 'CET/CEST',
  'Europe/Brussels': 'CET/CEST',
  'Europe/Vienna': 'CET/CEST',
  'Europe/Zurich': 'CET/CEST',
  'Europe/Prague': 'CET/CEST',
  'Europe/Budapest': 'CET/CEST',
  'Europe/Warsaw': 'CET/CEST',
  'Europe/Stockholm': 'CET/CEST',
  'Europe/Oslo': 'CET/CEST',
  'Europe/Copenhagen': 'CET/CEST',

  // Europe - Eastern
  'Europe/Athens': 'EET/EEST',
  'Europe/Helsinki': 'EET/EEST',
  'Europe/Istanbul': 'TRT',
  'Europe/Kiev': 'EET/EEST',
  'Europe/Bucharest': 'EET/EEST',
  'Europe/Sofia': 'EET/EEST',
  'Europe/Riga': 'EET/EEST',
  'Europe/Tallinn': 'EET/EEST',
  'Europe/Vilnius': 'EET/EEST',

  // Europe - Other
  'Europe/Moscow': 'MSK',
  'Europe/Minsk': 'MSK',
  'Europe/Kaliningrad': 'EET',

  // Asia - Middle East
  'Asia/Dubai': 'GST',
  'Asia/Riyadh': 'AST',
  'Asia/Kuwait': 'AST',
  'Asia/Bahrain': 'AST',
  'Asia/Qatar': 'AST',
  'Asia/Muscat': 'GST',
  'Asia/Jerusalem': 'IST/IDT',
  'Asia/Beirut': 'EET/EEST',
  'Asia/Damascus': 'EET/EEST',
  'Asia/Amman': 'EET/EEST',
  'Asia/Baghdad': 'AST',
  'Asia/Tehran': 'IRST/IRDT',

  // Asia - South Asia
  'Asia/Kolkata': 'IST',
  'Asia/Karachi': 'PKT',
  'Asia/Dhaka': 'BST',
  'Asia/Kathmandu': 'NPT',
  'Asia/Colombo': 'IST',

  // Asia - Southeast Asia
  'Asia/Bangkok': 'ICT',
  'Asia/Ho_Chi_Minh': 'ICT',
  'Asia/Jakarta': 'WIB',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Manila': 'PHT',
  'Asia/Yangon': 'MMT',
  'Asia/Phnom_Penh': 'ICT',
  'Asia/Vientiane': 'ICT',

  // Asia - East Asia
  'Asia/Shanghai': 'CST',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Taipei': 'CST',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Macau': 'CST',

  // Asia - Central Asia
  'Asia/Almaty': 'ALMT',
  'Asia/Tashkent': 'UZT',
  'Asia/Ashgabat': 'TMT',
  'Asia/Bishkek': 'KGT',
  'Asia/Dushanbe': 'TJT',

  // Asia - Other
  'Asia/Yekaterinburg': 'YEKT',
  'Asia/Novosibirsk': 'NOVT',
  'Asia/Krasnoyarsk': 'KRAT',
  'Asia/Irkutsk': 'IRKT',
  'Asia/Yakutsk': 'YAKT',
  'Asia/Vladivostok': 'VLAT',
  'Asia/Magadan': 'MAGT',

  // Africa - North
  'Africa/Cairo': 'EET',
  'Africa/Casablanca': 'WET',
  'Africa/Algiers': 'CET',
  'Africa/Tunis': 'CET',
  'Africa/Tripoli': 'EET',

  // Africa - West
  'Africa/Lagos': 'WAT',
  'Africa/Accra': 'GMT',
  'Africa/Dakar': 'GMT',
  'Africa/Abidjan': 'GMT',
  'Africa/Monrovia': 'GMT',

  // Africa - East
  'Africa/Nairobi': 'EAT',
  'Africa/Addis_Ababa': 'EAT',
  'Africa/Dar_es_Salaam': 'EAT',
  'Africa/Kampala': 'EAT',
  'Africa/Khartoum': 'CAT',

  // Africa - South
  'Africa/Johannesburg': 'SAST',
  'Africa/Maputo': 'CAT',
  'Africa/Harare': 'CAT',
  'Africa/Lusaka': 'CAT',
  'Africa/Windhoek': 'CAT',

  // Australia & Pacific
  'Australia/Sydney': 'AEDT/AEST',
  'Australia/Melbourne': 'AEDT/AEST',
  'Australia/Brisbane': 'AEST',
  'Australia/Perth': 'AWST',
  'Australia/Adelaide': 'ACDT/ACST',
  'Australia/Darwin': 'ACST',
  'Australia/Hobart': 'AEDT/AEST',
  'Pacific/Auckland': 'NZDT/NZST',
  'Pacific/Fiji': 'FJT',
  'Pacific/Guam': 'ChST',
  'Pacific/Honolulu': 'HST',
  'Pacific/Tahiti': 'TAHT',
  'Pacific/Port_Moresby': 'PGT',
  'Pacific/Noumea': 'NCT',
  'Pacific/Tongatapu': 'TOT',
  'Pacific/Samoa': 'SST',

  // Atlantic
  'Atlantic/Cape_Verde': 'CVT',
  'Atlantic/Canary': 'WET/WEST',
  'Atlantic/Bermuda': 'AST/ADT',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Converts a number to an ordinal string (1st, 2nd, 3rd, etc.)
 */
const getOrdinalSuffix = (num: number): string => {
  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return `${num}st`;
  }
  if (j === 2 && k !== 12) {
    return `${num}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${num}rd`;
  }
  return `${num}th`;
};

/**
 * Converts UTC offset string to readable format
 * Example: "-14400s" -> "-04:00"
 */
const formatUtcOffset = (offset: string): string => {
  const match = offset.match(/^(-?\d+)s?$/);
  if (!match) return offset;

  const seconds = parseInt(match[1], 10);
  const absSeconds = Math.abs(seconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);

  const sign = seconds < 0 ? '-' : '+';
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');

  return `${sign}${hoursStr}:${minutesStr}`;
};

/**
 * Gets timezone abbreviation from IANA timezone ID
 * Comprehensive mapping of IANA timezone IDs to their abbreviations
 */
const getTimezoneAbbreviation = (timezoneId: string): string => {
  return TIMEZONE_ABBREVIATIONS[timezoneId] || timezoneId;
};

/**
 * Pluralizes a unit name based on count
 */
const pluralize = (count: number, unit: string): string => {
  return count === 1 ? unit : `${unit}s`;
};

// ============================================================================
// Public Formatting Functions
// ============================================================================

/**
 * Formats a date to a human-readable string
 *
 * Examples:
 * - Full date (year, month, day): "October 30, 2024"
 * - Year-month only: "December 2024"
 * - Month-day only: "July 4th"
 *
 * @param date - The date object to format
 * @returns Human-readable date string
 */
export function formatDateDisplay(date: TypeDate): string {
  const year = date.year ?? 0;
  const month = date.month ?? 0;
  const day = date.day ?? 0;

  // Full date
  if (year > 0 && month > 0 && day > 0) {
    // Use dayjs to create a date and format it
    const dayjsDate = dayjs(
      `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
    );
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${day}, ${year}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${day}, ${year}`;
  }

  // Year-month only (e.g., credit card expiration)
  if (year > 0 && month > 0 && day === 0) {
    const dayjsDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`);
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${year}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${year}`;
  }

  // Month-day only (e.g., anniversary)
  if (year === 0 && month > 0 && day > 0) {
    // Use a reference year for dayjs (2000 is a leap year, good for validation)
    const dayjsDate = dayjs(
      `2000-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
    );
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${getOrdinalSuffix(day)}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${getOrdinalSuffix(day)}`;
  }

  // Fallback to ISO-like format with what's available
  const parts: string[] = [];
  if (year > 0) parts.push(year.toString().padStart(4, '0'));
  if (month > 0) parts.push(month.toString().padStart(2, '0'));
  if (day > 0) parts.push(day.toString().padStart(2, '0'));

  return parts.join('-') || '-';
}

/**
 * Formats a time to a human-readable string
 *
 * Examples:
 * - Without seconds: "2:30 PM"
 * - With seconds: "2:30:45 PM"
 * - With milliseconds: "2:30:45.123 PM"
 *
 * @param time - The time object to format
 * @returns Human-readable time string
 */
export function formatTimeDisplay(time: TypeTimeOfDay): string {
  const hours = time.hours ?? 0;
  const minutes = time.minutes ?? 0;
  const seconds = time.seconds ?? 0;
  const nanos = time.nanos ?? 0;

  // Use dayjs to create a time and format it
  // Create a reference date (2000-01-01) and set the time
  const dayjsTime = dayjs(
    `2000-01-01T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
  );

  if (!dayjsTime.isValid()) {
    // Fallback to manual formatting if invalid
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours12}:${minutesStr} ${period}`;
  }

  // If no seconds and no nanos, show simple format
  if (seconds === 0 && nanos === 0) {
    return dayjsTime.format('h:mm A');
  }

  // If we have nanos, convert to milliseconds and format
  if (nanos > 0) {
    const milliseconds = Math.floor(nanos / 1000000);
    const msStr = milliseconds.toString().padStart(3, '0');
    return `${dayjsTime.format('h:mm:ss')}.${msStr} ${dayjsTime.format('A')}`;
  }

  // Just seconds
  return dayjsTime.format('h:mm:ss A');
}

/**
 * Formats a datetime to a human-readable string with optional timezone
 *
 * Examples:
 * - Simple: "Oct 30, 2024 2:30 PM"
 * - With timezone: "Oct 30, 2024 2:30 PM EST/EDT"
 * - With UTC offset: "Oct 30, 2024 2:30 PM -04:00"
 *
 * @param datetime - The datetime object to format
 * @returns Human-readable datetime string
 */
export function formatDateTimeDisplay(datetime: TypeDateTime): string {
  const year = datetime.year ?? 0;
  const month = datetime.month ?? 0;
  const day = datetime.day ?? 0;
  const hours = datetime.hours ?? 0;
  const minutes = datetime.minutes ?? 0;
  const seconds = datetime.seconds ?? 0;
  const nanos = datetime.nanos ?? 0;

  // Build ISO-like string for dayjs parsing
  const dateStr = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  const isoStr = `${dateStr}T${timeStr}`;

  let dayjsDateTime = dayjs(isoStr);

  // Handle timezone if available
  if (datetime.time_zone?.id) {
    try {
      dayjsDateTime = dayjsDateTime.tz(datetime.time_zone.id);
    } catch {
      // If timezone is invalid, use UTC
      dayjsDateTime = dayjsDateTime.utc();
    }
  } else if (datetime.utc_offset) {
    // Parse UTC offset and apply it
    const offsetMatch = datetime.utc_offset.match(/^(-?\d+)s?$/);
    if (offsetMatch) {
      const offsetSeconds = parseInt(offsetMatch[1], 10);
      const offsetHours = Math.floor(Math.abs(offsetSeconds) / 3600);
      const offsetMinutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
      const sign = offsetSeconds < 0 ? '-' : '+';
      const offsetStr = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
      dayjsDateTime = dayjsDateTime.utcOffset(offsetStr);
    }
  }

  if (!dayjsDateTime.isValid()) {
    // Fallback to manual formatting if invalid
    const monthName =
      month > 0 ? MONTH_NAMES_SHORT[month - 1] : month.toString();
    const datePart =
      year > 0 && month > 0 && day > 0
        ? `${monthName} ${day}, ${year}`
        : `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const minutesStr = minutes.toString().padStart(2, '0');
    let timePart = `${hours12}:${minutesStr}`;

    if (seconds > 0 || nanos > 0) {
      const secondsStr = seconds.toString().padStart(2, '0');
      timePart += `:${secondsStr}`;
      if (nanos > 0) {
        const milliseconds = Math.floor(nanos / 1000000);
        const msStr = milliseconds.toString().padStart(3, '0');
        timePart += `.${msStr}`;
      }
    }

    timePart += ` ${period}`;

    let timezonePart = '';
    if (datetime.time_zone?.id) {
      const tzAbbr = getTimezoneAbbreviation(datetime.time_zone.id);
      timezonePart = ` ${tzAbbr}`;
    } else if (datetime.utc_offset) {
      const offset = formatUtcOffset(datetime.utc_offset);
      timezonePart = ` ${offset}`;
    }

    return `${datePart} ${timePart}${timezonePart}`;
  }

  // Format date part using dayjs
  const datePart =
    year > 0 && month > 0 && day > 0
      ? dayjsDateTime.format('MMM D, YYYY')
      : `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

  // Format time part using dayjs
  let timePart = '';
  if (seconds > 0 || nanos > 0) {
    if (nanos > 0) {
      const milliseconds = Math.floor(nanos / 1000000);
      const msStr = milliseconds.toString().padStart(3, '0');
      timePart = `${dayjsDateTime.format('h:mm:ss')}.${msStr} ${dayjsDateTime.format('A')}`;
    } else {
      timePart = dayjsDateTime.format('h:mm:ss A');
    }
  } else {
    timePart = dayjsDateTime.format('h:mm A');
  }

  // Add timezone if available
  let timezonePart = '';
  if (datetime.time_zone?.id) {
    const tzAbbr = getTimezoneAbbreviation(datetime.time_zone.id);
    timezonePart = ` ${tzAbbr}`;
  } else if (datetime.utc_offset) {
    const offset = formatUtcOffset(datetime.utc_offset);
    timezonePart = ` ${offset}`;
  }

  return `${datePart} ${timePart}${timezonePart}`;
}

/**
 * Formats a timezone to a human-readable string
 *
 * Examples:
 * - "EST/EDT" for America/New_York
 * - "UTC" for UTC
 * - Returns original ID if no mapping found
 *
 * @param timezone - The timezone object to format
 * @returns Human-readable timezone string
 */
export function formatTimeZoneDisplay(timezone: TypeTimeZone): string {
  const id = timezone.id ?? '';
  if (!id) return '-';

  return getTimezoneAbbreviation(id);
}

/**
 * Formats a duration string to a human-readable format
 *
 * Examples:
 * - "3600s" -> "1 hour"
 * - "120s" -> "2 minutes"
 * - "86400.500s" -> "1 day 500ms"
 * - "90s" -> "1 minute 30 seconds"
 *
 * @param duration - Duration string in format "Xs" where X is seconds (can be decimal)
 * @returns Human-readable duration string
 */
export function formatDurationDisplay(duration: string): string {
  // Parse the duration string (format: "123.456s" or "123s")
  const match = duration.match(/^(-?\d+\.?\d*)s?$/);
  if (!match) return duration;

  const totalSeconds = parseFloat(match[1]);
  if (isNaN(totalSeconds)) return duration;

  const isNegative = totalSeconds < 0;
  const absSeconds = Math.abs(totalSeconds);

  // Use dayjs duration to parse and break down
  const dayjsDuration = dayjs.duration(absSeconds, 'seconds');

  const days = Math.floor(dayjsDuration.asDays());
  const hours = dayjsDuration.hours();
  const minutes = dayjsDuration.minutes();
  const seconds = dayjsDuration.seconds();
  const milliseconds = dayjsDuration.milliseconds();

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${pluralize(days, 'day')}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${pluralize(hours, 'hour')}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${pluralize(minutes, 'minute')}`);
  }
  if (seconds > 0) {
    parts.push(`${seconds} ${pluralize(seconds, 'second')}`);
  }
  if (milliseconds > 0) {
    parts.push(`${milliseconds}ms`);
  }

  // If nothing, it's 0 seconds
  if (parts.length === 0) {
    return '0 seconds';
  }

  const result = parts.join(' ');
  return isNegative ? `-${result}` : result;
}

/**
 * Formats a date to ISO format (YYYY-MM-DD)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('0000' for year, '01' for month/day).
 *
 * @param date - The date object to format
 * @returns ISO date string (YYYY-MM-DD)
 */
export function formatDateISO(date: TypeDate): string {
  const year =
    date.year !== undefined ? date.year.toString().padStart(4, '0') : '0000';
  const month =
    date.month !== undefined ? date.month.toString().padStart(2, '0') : '01';
  const day =
    date.day !== undefined ? date.day.toString().padStart(2, '0') : '01';
  return `${year}-${month}-${day}`;
}

/**
 * Formats a datetime to ISO format (YYYY-MM-DDTHH:mm)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('0000' for year, '01' for month/day, '00' for time).
 *
 * @param datetime - The datetime object to format
 * @returns ISO datetime string (YYYY-MM-DDTHH:mm)
 */
export function formatDateTimeISO(datetime: TypeDateTime): string {
  const year =
    datetime.year !== undefined
      ? datetime.year.toString().padStart(4, '0')
      : '0000';
  const month =
    datetime.month !== undefined
      ? datetime.month.toString().padStart(2, '0')
      : '01';
  const day =
    datetime.day !== undefined
      ? datetime.day.toString().padStart(2, '0')
      : '01';
  const hours =
    datetime.hours !== undefined
      ? datetime.hours.toString().padStart(2, '0')
      : '00';
  const minutes =
    datetime.minutes !== undefined
      ? datetime.minutes.toString().padStart(2, '0')
      : '00';
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats a time to ISO format (HH:mm)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('00' for hours/minutes).
 *
 * @param time - The time object to format
 * @returns ISO time string (HH:mm)
 */
export function formatTimeISO(time: TypeTimeOfDay): string {
  const hours =
    time.hours !== undefined ? time.hours.toString().padStart(2, '0') : '00';
  const minutes =
    time.minutes !== undefined
      ? time.minutes.toString().padStart(2, '0')
      : '00';
  return `${hours}:${minutes}`;
}

/**
 * Formats a timestamp string (ISO 8601) to a human-readable format
 * Used for displaying timestamps from API responses
 *
 * Uses dayjs format strings directly. See https://day.js.org/docs/en/display/format
 * Common format tokens:
 * - Date: YYYY (year), YY (2-digit year), MMMM (month name), MMM (short month), MM (month number), DD (day), dddd (day name), ddd (short day), d (day of week 0-6)
 * - Time: HH (24-hour), hh (12-hour), mm (minutes), ss (seconds), A (AM/PM)
 *
 * @param timestamp - ISO 8601 timestamp string (e.g., "2024-10-30T14:30:00Z")
 * @param format - Format string in dayjs format (default: "MM/DD/YYYY hh:mm A")
 * @returns Formatted date string
 */
export function formatTimestamp(
  timestamp: string,
  format: string = 'MM/DD/YYYY hh:mm A',
): string {
  const dayjsDate = dayjs(timestamp);
  if (!dayjsDate.isValid()) {
    return timestamp; // Return original if invalid
  }

  return dayjsDate.format(format);
}

/**
 * Formats a Date object or timestamp string to locale string
 * Used for displaying dates in a locale-aware format
 *
 * @param date - Date object, timestamp string, or undefined
 * @returns Formatted date string or empty string
 */
export function formatDateLocale(date: string | Date | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') {
    const dayjsDate = dayjs(date);
    if (dayjsDate.isValid()) {
      return dayjsDate.format('M/D/YYYY, h:mm:ss A');
    }
    return date;
  }
  // Date object
  const dayjsDate = dayjs(date);
  if (dayjsDate.isValid()) {
    return dayjsDate.format('M/D/YYYY, h:mm:ss A');
  }
  return '';
}

/**
 * Parses a date string (YYYY-MM-DD) to TypeDate
 * Used for converting ISO date strings to TypeDate objects
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns TypeDate object
 * @throws {Error} If the date string format is invalid or contains invalid values
 */
export function parseDate(dateStr: string): TypeDate {
  // Validate format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error(
      `Invalid date format: "${dateStr}". Expected format: YYYY-MM-DD`,
    );
  }

  const parts = dateStr.split('-');
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10);
  const day = parseInt(parts[2]!, 10);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(
      `Invalid date values in: "${dateStr}". All components must be valid numbers.`,
    );
  }

  return { year, month, day };
}

/**
 * Parses a datetime string (YYYY-MM-DDTHH:mm) to TypeDateTime
 * Used for converting ISO datetime strings to TypeDateTime objects
 *
 * @param datetimeStr - Datetime string in YYYY-MM-DDTHH:mm format
 * @returns TypeDateTime object
 * @throws {Error} If the datetime string format is invalid or contains invalid values
 */
export function parseDateTime(datetimeStr: string): TypeDateTime {
  // Validate format
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(datetimeStr)) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Expected format: YYYY-MM-DDTHH:mm`,
    );
  }

  const [datePart, timePart] = datetimeStr.split('T');
  if (!datePart || !timePart) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Missing date or time component.`,
    );
  }

  const dateParts = datePart.split('-');
  const timeParts = timePart.split(':');

  if (dateParts.length !== 3 || timeParts.length !== 2) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Date must have 3 parts, time must have 2 parts.`,
    );
  }

  const year = parseInt(dateParts[0]!, 10);
  const month = parseInt(dateParts[1]!, 10);
  const day = parseInt(dateParts[2]!, 10);
  const hours = parseInt(timeParts[0]!, 10);
  const minutes = parseInt(timeParts[1]!, 10);

  // Validate parsed values
  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hours) ||
    isNaN(minutes)
  ) {
    throw new Error(
      `Invalid datetime values in: "${datetimeStr}". All components must be valid numbers.`,
    );
  }

  // Validate ranges
  if (hours < 0 || hours > 23) {
    throw new Error(
      `Invalid hours value: ${hours}. Hours must be between 0 and 23.`,
    );
  }
  if (minutes < 0 || minutes > 59) {
    throw new Error(
      `Invalid minutes value: ${minutes}. Minutes must be between 0 and 59.`,
    );
  }

  return { year, month, day, hours, minutes, seconds: 0, nanos: 0 };
}

/**
 * Parses a time string (HH:mm) to TypeTimeOfDay
 * Used for converting time strings to TypeTimeOfDay objects
 *
 * @param timeStr - Time string in HH:mm format
 * @returns TypeTimeOfDay object
 * @throws {Error} If the time string format is invalid or contains invalid values
 */
export function parseTime(timeStr: string): TypeTimeOfDay {
  // Validate format
  if (!/^\d{2}:\d{2}$/.test(timeStr)) {
    throw new Error(
      `Invalid time format: "${timeStr}". Expected format: HH:mm`,
    );
  }

  const parts = timeStr.split(':');
  if (parts.length !== 2) {
    throw new Error(
      `Invalid time format: "${timeStr}". Time must have 2 parts (hours:minutes).`,
    );
  }

  const hours = parseInt(parts[0]!, 10);
  const minutes = parseInt(parts[1]!, 10);

  // Validate parsed values
  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error(
      `Invalid time values in: "${timeStr}". Hours and minutes must be valid numbers.`,
    );
  }

  // Validate ranges
  if (hours < 0 || hours > 23) {
    throw new Error(
      `Invalid hours value: ${hours}. Hours must be between 0 and 23.`,
    );
  }
  if (minutes < 0 || minutes > 59) {
    throw new Error(
      `Invalid minutes value: ${minutes}. Minutes must be between 0 and 59.`,
    );
  }

  return { hours, minutes, seconds: 0, nanos: 0 };
}

/**
 * Validates if a date string is a valid date
 * Used for validation purposes (e.g., in Zod schemas)
 * This function is lenient - dayjs wraps invalid dates (e.g., '2024-13-45' becomes '2025-02-14')
 *
 * @param dateStr - Date string to validate
 * @returns true if valid, false otherwise
 */
export function isValidDate(dateStr: string): boolean {
  const dayjsDate = dayjs(dateStr);
  return dayjsDate.isValid();
}

/**
 * Strictly validates if a date string represents a valid date without wrapping
 * Used for input validation where we want to reject invalid dates like '2024-13-45'
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns true if valid and matches the input exactly, false otherwise
 */
export function isValidDateStrict(dateStr: string): boolean {
  // Parse the date string
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const [, yearStr, monthStr, dayStr] = match;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Check if month and day are in valid ranges
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Handle year 0 edge case - JavaScript Date doesn't handle year 0 well
  // For year 0, we'll manually validate based on month/day ranges
  if (year === 0) {
    // Check if day is valid for the given month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
  }

  // Create a date object and check if it matches the input
  // This will catch invalid dates like Feb 30, Apr 31, etc.
  const date = new Date(year, month - 1, day);

  // Check if the date components match (this catches invalid dates that get wrapped)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  return true;
}

/**
 * Strictly validates if a datetime string represents a valid datetime without wrapping
 * Used for input validation where we want to reject invalid datetimes like '2024-13-45T25:99'
 *
 * @param datetimeStr - Datetime string in YYYY-MM-DDTHH:mm format
 * @returns true if valid and matches the input exactly, false otherwise
 */
export function isValidDateTimeStrict(datetimeStr: string): boolean {
  // Parse the datetime string
  const match = datetimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return false;

  const [, yearStr, monthStr, dayStr, hoursStr, minutesStr] = match;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Check if date components are valid
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Check if time components are valid
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  // Handle year 0 edge case - JavaScript Date doesn't handle year 0 well
  // For year 0, we'll manually validate based on month/day ranges
  if (year === 0) {
    // Check if day is valid for the given month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
  }

  // Create a date object and check if it matches the input
  const date = new Date(year, month - 1, day, hours, minutes);

  // Check if the date components match (this catches invalid dates that get wrapped)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hours ||
    date.getMinutes() !== minutes
  ) {
    return false;
  }

  return true;
}

/**
 * Gets the current timestamp as a Date object
 * Used when you need a Date object for timestamps (e.g., in stores, messages)
 *
 * @returns Current Date object
 */
export function getCurrentDate(): Date {
  return new Date();
}

/**
 * Gets the date one week ago from today as a Date object
 * Used when you need a Date object for date ranges (e.g., filtering data from the past week)
 *
 * @returns Date object representing one week ago
 */
export function getDateOneWeekAgo(): Date {
  return dayjs().subtract(1, 'week').toDate();
}

/**
 * Gets the start of the current week (Monday) as a Date object
 * Used when you need a Date object for week-based date ranges
 *
 * @returns Date object representing the start of the current week (Monday)
 */
export function getStartOfWeek(): Date {
  return dayjs().startOf('isoWeek').toDate();
}

/**
 * Converts a Date object, timestamp string, or undefined to a numeric timestamp.
 * Used for sorting and comparing timestamps when the value may be in different formats.
 *
 * @param timestamp - Date object, ISO timestamp string, or undefined
 * @returns Numeric timestamp in milliseconds (0 if undefined or invalid)
 *
 * @example
 * getTimestamp(new Date('2024-01-15')) // Returns: 1705276800000
 * getTimestamp('2024-01-15T14:30:00Z') // Returns: 1705336200000
 * getTimestamp(undefined) // Returns: 0
 */
export function getTimestamp(timestamp: Date | string | undefined): number {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  return Date.parse(timestamp) || 0;
}

/**
 * Parses an ISO 8601 timestamp string to a Date object.
 * Used for converting API response timestamps (ISO strings) to Date objects.
 *
 * @param timestamp - ISO 8601 timestamp string (e.g., "2024-01-15T14:30:00Z")
 * @returns Date object parsed from the timestamp string
 *
 * @example
 * parseTimestamp('2024-01-15T14:30:00Z') // Returns Date object
 */
export function parseTimestamp(timestamp: string): Date {
  return new Date(timestamp);
}

/**
 * Formats a Date object to a string using the specified format
 * Used for formatting Date objects directly (e.g., in date pickers)
 *
 * Uses dayjs format strings directly. See https://day.js.org/docs/en/display/format
 * Common format tokens:
 * - Date: YYYY (year), YY (2-digit year), MMMM (month name), MMM (short month), MM (month number), DD (day), dddd (day name), ddd (short day), d (day of week 0-6)
 * - Time: HH (24-hour), hh (12-hour), mm (minutes), ss (seconds), A (AM/PM)
 *
 * @param date - Date object to format
 * @param format - Format string in dayjs format (default: "MMMM D, YYYY")
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | undefined,
  format: string = 'MMMM D, YYYY',
): string {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '';
  }

  return dayjsDate.format(format);
}

/**
 * Formats a date with relative labels for today and yesterday.
 * Returns "Today", "Yesterday", or a formatted date string without time.
 *
 * @param date - Date object to format
 * @returns "Today", "Yesterday", or formatted date string (e.g., "Dec 9, 2025")
 *
 * @example
 * formatRelativeDate(new Date()); // "Today"
 * formatRelativeDate(addDays(new Date(), -1)); // "Yesterday"
 * formatRelativeDate(addDays(new Date(), -5)); // "Dec 9, 2025"
 */
export function formatRelativeDate(date: Date | undefined): string {
  if (!date) return '';

  const now = getCurrentDate();
  const todayKey = dateToISOString(now);
  const yesterdayKey = dateToISOString(addDays(now, -1));
  const dateKey = dateToISOString(date);

  if (dateKey === todayKey) {
    return i18n.t('common.today', { ns: 'common' });
  }

  if (dateKey === yesterdayKey) {
    return i18n.t('common.yesterday', { ns: 'common' });
  }

  return formatDate(date, DATE_FORMAT_TOKENS.SHORT_DATE);
}

/**
 * Formats a date with relative labels for today and yesterday, including time.
 * Returns "Today", "Yesterday", or a formatted date string with time.
 *
 * @param date - Date object to format
 * @returns "Today", "Yesterday", or formatted date string with time (e.g., "Dec 9, 3:30 PM")
 *
 * @example
 * formatRelativeDateWithTime(new Date()); // "Today"
 * formatRelativeDateWithTime(addDays(new Date(), -1)); // "Yesterday"
 * formatRelativeDateWithTime(addDays(new Date(), -5)); // "Dec 9, 3:30 PM"
 */
export function formatRelativeDateWithTime(date: Date | undefined): string {
  if (!date) return '';

  const now = getCurrentDate();
  const todayKey = dateToISOString(now);
  const yesterdayKey = dateToISOString(addDays(now, -1));
  const dateKey = dateToISOString(date);

  if (dateKey === todayKey) {
    return i18n.t('common.today', { ns: 'common' });
  }

  if (dateKey === yesterdayKey) {
    return i18n.t('common.yesterday', { ns: 'common' });
  }

  return formatDate(date, DATE_FORMAT_TOKENS.SHORT_DATE_TIME);
}

/**
 * Parses a date string (YYYY-MM-DD) to a Date object.
 * Used for converting ISO date strings to Date objects for date calculations.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object representing the parsed date
 */
export function parseDateString(dateStr: string): Date {
  return dayjs(dateStr).toDate();
}

/**
 * Normalizes a date to the start of the day (midnight) for accurate date-only comparisons.
 * Used when comparing dates without considering time components.
 *
 * @param date - Date to normalize
 * @param timezone - Optional IANA timezone ID (e.g., 'America/New_York', 'UTC'). If provided, the date will be normalized in that timezone.
 * @returns New Date object set to midnight (00:00:00) of the same day in the specified timezone
 */
export function normalizeToStartOfDay(date: Date, timezone?: string): Date {
  let dayjsDate = dayjs(date);
  if (timezone) {
    dayjsDate = dayjsDate.tz(timezone);
  }
  return dayjsDate.startOf('day').toDate();
}

/**
 * Normalizes a date to the end of the day (23:59:59.999) for accurate date range filtering.
 * Used when filtering data to include all records from a specific day.
 *
 * @param date - Date to normalize
 * @param timezone - Optional IANA timezone ID (e.g., 'America/New_York', 'UTC'). If provided, the date will be normalized in that timezone.
 * @returns New Date object set to end of day (23:59:59.999) of the same day in the specified timezone
 */
export function normalizeToEndOfDay(date: Date, timezone?: string): Date {
  let dayjsDate = dayjs(date);
  if (timezone) {
    dayjsDate = dayjsDate.tz(timezone);
  }
  return dayjsDate.endOf('day').toDate();
}

/**
 * Calculates the number of days between two dates.
 * Used for date range calculations and grouping logic.
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Number of days between the two dates
 */
export function getDaysDifference(date1: Date, date2: Date): number {
  return Math.floor(
    (date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24),
  );
}

/**
 * Adds a specified number of days to a date.
 * Used for calculating date ranges and group boundaries.
 *
 * @param date - Base date
 * @param days - Number of days to add (can be negative to subtract)
 * @returns New Date object with days added
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Gets the date range for a grouped period based on a reference date and grouping interval.
 * Used for grouping daily data into 5-day or 2-week periods.
 *
 * @param entryDate - The date to find the group for
 * @param periodStartDate - The start date of the overall period
 * @param groupSizeDays - Size of each group in days (e.g., 5 for 5-day groups, 14 for 2-week groups)
 * @returns Object with startDate and endDate as Date objects for the group
 */
export function getDateGroupRange(
  entryDate: Date,
  periodStartDate: Date,
  groupSizeDays: number,
): { startDate: Date; endDate: Date } {
  const daysSinceStart = getDaysDifference(entryDate, periodStartDate);
  const groupIndex = Math.floor(daysSinceStart / groupSizeDays);
  const groupStartDate = addDays(periodStartDate, groupIndex * groupSizeDays);
  const groupEndDate = addDays(groupStartDate, groupSizeDays - 1);
  return { startDate: groupStartDate, endDate: groupEndDate };
}

/**
 * Formats a date range label for display.
 * Used for x-axis labels in charts showing grouped date ranges.
 *
 * @param startDateStr - Start date string in YYYY-MM-DD format
 * @param endDateStr - End date string in YYYY-MM-DD format
 * @returns Formatted date range string (e.g., "Jan 1 - Jan 5" or "Jan 1 - 5")
 */
export function formatDateRangeLabel(
  startDateStr: string,
  endDateStr: string,
): string {
  const start = parseDateString(startDateStr);
  const end = parseDateString(endDateStr);
  const startFormatted = formatTimestamp(startDateStr, 'MMM D');
  const endFormatted = formatTimestamp(endDateStr, 'MMM D');

  // If same month, show "MMM D - D" format, otherwise "MMM D - MMM D"
  if (start.getMonth() === end.getMonth()) {
    const endDay = formatTimestamp(endDateStr, 'D');
    return `${startFormatted} - ${endDay}`;
  }
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Converts a Date object to an ISO date string (YYYY-MM-DD).
 * Used for converting Date objects back to string format for storage or comparison.
 *
 * @param date - Date object to convert
 * @returns ISO date string in YYYY-MM-DD format
 */
export function dateToISOString(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}
